import React, { useCallback, useEffect, useMemo, useState } from "react";
import LatexRenderer from "./LatexRenderer";
import { HINT_LEVELS } from "../data/hintResources";
import { parseMathText } from "../utils/parseMathText";
import { formatHintText } from "../utils/hintText";

const HintManager = ({
  problem,
  hints = [],
  revealAll = false,
  hintCheckResponses = [],
  onSubmitCheck,
  isLocked = false,
}) => {
  const loggedLevels = useMemo(() => {
    if (revealAll) {
      return new Set(HINT_LEVELS.map((entry) => entry.level));
    }
    return new Set((problem?.hintTimeline || []).map((entry) => entry.level));
  }, [problem?.hintTimeline, revealAll]);

  const [expandedLevels, setExpandedLevels] = useState({});
  const [responseDrafts, setResponseDrafts] = useState({});
  const [checkSubmitting, setCheckSubmitting] = useState({});

  const normalizedCheckResponses = useMemo(
    () => (Array.isArray(hintCheckResponses) ? hintCheckResponses : []),
    [hintCheckResponses]
  );
  const latestCheckByLevel = useMemo(() => {
    return normalizedCheckResponses.reduce((acc, entry) => {
      const level = Number(entry?.level);
      if (!Number.isFinite(level)) return acc;
      const current = acc[level];
      if (!current || (entry.timestamp || 0) >= (current.timestamp || 0)) {
        acc[level] = entry;
      }
      return acc;
    }, {});
  }, [normalizedCheckResponses]);

  useEffect(() => {
    setExpandedLevels((prev) => {
      const next = { ...prev };
      loggedLevels.forEach((level) => {
        if (next[level] === undefined) {
          next[level] = false;
        }
      });
      return next;
    });
  }, [loggedLevels]);

  const latestLevel = useMemo(() => {
    if (revealAll) return HINT_LEVELS.length;
    if (!loggedLevels.size) return 0;
    return Math.max(...Array.from(loggedLevels).map((value) => Number(value) || 0));
  }, [loggedLevels, revealAll]);

  const latestUnlockedLevel = useMemo(() => {
    if (!loggedLevels.size) return null;
    return Math.max(...Array.from(loggedLevels).map((value) => Number(value) || 0));
  }, [loggedLevels]);

  const activeResponseLevel = useMemo(() => {
    if (!latestUnlockedLevel) return null;
    if (latestCheckByLevel[latestUnlockedLevel]) return null;
    return latestUnlockedLevel;
  }, [latestCheckByLevel, latestUnlockedLevel]);

  const hintsGenerating = !!problem?.hintsGenerating;
  const allUnlocked = loggedLevels.size >= HINT_LEVELS.length;

  const handleSubmitCheck = useCallback(
    async (level, responseType) => {
      if (!onSubmitCheck || isLocked) return;
      const draft = String(responseDrafts[level] || "").trim();
      const mode = responseType === "dont_know" ? "dont_know" : "text";
      if (mode === "text" && !draft) return;

      setCheckSubmitting((prev) => ({ ...prev, [level]: true }));
      try {
        await onSubmitCheck(level, mode === "dont_know" ? "" : draft, mode);
        setResponseDrafts((prev) => ({ ...prev, [level]: "" }));
      } finally {
        setCheckSubmitting((prev) => ({ ...prev, [level]: false }));
      }
    },
    [isLocked, onSubmitCheck, responseDrafts]
  );

  if (!problem) return null;

  return (
    <div className="hint-manager">
      <div className="hint-manager__header">
        <div className="stack stack--dense" style={{ gap: "4px" }}>
          <p style={{ margin: 0, fontWeight: 700 }}>
            Hints {latestLevel ? `· ${latestLevel}/5` : ""}
          </p>
          {allUnlocked && (
            <p className="mini-label" style={{ margin: 0 }}>
              All hints unlocked.
            </p>
          )}
          {hintsGenerating && (
            <p className="mini-label" style={{ margin: 0 }}>
              Generating hints…
            </p>
          )}
        </div>
      </div>

      <div className="hint-levels">
        {HINT_LEVELS.map((entry) => {
          if (!loggedLevels.has(entry.level)) return null;
          const hint = hints.find((h) => h.level === entry.level);
          const hintText = formatHintText(hint?.text?.trim() || "");
          const isExpanded = expandedLevels[entry.level] !== false;
          const hintParts = isExpanded ? parseMathText(hintText) : [];
          const savedCheck = latestCheckByLevel[entry.level] || null;
          const checkDraft = responseDrafts[entry.level] || "";
          const isSubmitting = !!checkSubmitting[entry.level];
          const checkLocked = isLocked || !onSubmitCheck;
          const showResponseInput =
            entry.level === activeResponseLevel && !revealAll;

          return (
            <div key={entry.level} className="hint-card hint-card--unlocked">
              <div className="hint-card__header">
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    Level {entry.level}
                  </p>
                  <p className="mini-label" style={{ margin: 0 }}>
                    {entry.description}
                  </p>
                </div>
                {hintText ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() =>
                      setExpandedLevels((prev) => ({
                        ...prev,
                        [entry.level]: !isExpanded,
                      }))
                    }
                  >
                    {isExpanded ? "Hide" : "Show"}
                  </button>
                ) : null}
              </div>

              {isExpanded && (
                <div className="hint-card__body">
                  <div className="hint-card__content hint-card__content--rich">
                    {hintText ? (
                      hintParts.map((part, index) => {
                        if (part.type === "latex") {
                          const className = part.displayMode
                            ? "hint-latex hint-latex--block"
                            : "hint-latex hint-latex--inline";
                          const Wrapper = part.displayMode ? "div" : "span";
                          return (
                            <Wrapper
                              key={`latex-${entry.level}-${index}`}
                              className={className}
                            >
                              <LatexRenderer
                                latex={part.value}
                                displayMode={part.displayMode}
                              />
                            </Wrapper>
                          );
                        }
                        return (
                          <span key={`text-${entry.level}-${index}`}>
                            {part.value}
                          </span>
                        );
                      })
                    ) : (
                      <p className="mini-label" style={{ margin: 0 }}>
                        {hintsGenerating ? "Still generating..." : "Hint not available."}
                      </p>
                    )}
                  </div>

                  {savedCheck && !showResponseInput && (
                    <p className="mini-label hint-response__saved">
                      {savedCheck.responseType === "dont_know"
                        ? "Update saved: I don't know."
                        : "Progress update saved."}
                    </p>
                  )}

                  {showResponseInput && (
                    <div className="hint-response">
                      <label className="mini-label" style={{ margin: 0 }}>
                        Where are you at, and what are you trying next?
                      </label>
                      <textarea
                        rows={3}
                        value={checkDraft}
                        onChange={(event) =>
                          setResponseDrafts((prev) => ({
                            ...prev,
                            [entry.level]: event.target.value,
                          }))
                        }
                        placeholder="Share your current approach..."
                        aria-label={`Progress update for hint ${entry.level}`}
                        disabled={checkLocked || isSubmitting}
                        className="hint-textarea"
                      />
                      <div className="cluster" style={{ gap: "8px" }}>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => handleSubmitCheck(entry.level, "text")}
                          disabled={checkLocked || isSubmitting || !checkDraft.trim()}
                        >
                          {isSubmitting ? "Saving..." : "Submit update"}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => handleSubmitCheck(entry.level, "dont_know")}
                          disabled={checkLocked || isSubmitting}
                        >
                          I don't know
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HintManager;
